import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MagnifyingGlass,
  X,
  Briefcase,
  Users,
  FileText,
  BookOpen,
  Gavel,
  House,
  CalendarBlank,
  ChatTeardropDots,
  ClipboardText,
  Plus,
  ArrowRight,
  CircleNotch,
  Scales,
  CurrencyGbp,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { canCreateMatter } from '@/lib/pupilUiBoundary';
import { useDebounce } from '@/lib/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface SearchResult {
  id: string;
  label: string;
  sub: string;
  category: string;
  to?: string;
}

interface QuickAction {
  id: string;
  label: string;
  sub: string;
  Icon: React.ElementType;
  to: string;
  group: 'navigate' | 'create';
}

interface Props {
  onClose: () => void;
}

export function CommandPalette({ onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const debouncedQ = useDebounce(query.trim(), 250);

  const isAdmin = user?.memberType === 'admin';
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isSolicitor = user?.memberType === 'solicitor';
  const basePath = isAdmin ? '/admin' : isClerk ? '/clerk' : isSolicitor ? '/solicitor' : '/barrister';
  const isBarrister = !isAdmin && !isClerk && !isSolicitor;
  // Route names differ by role: admin and the solicitor portal have a calendar; clerk and
  // barrister have a diary. `${basePath}/diary` sent a solicitor to /solicitor/diary, which is
  // not a route — the catch-all then put them on the public marketing site.
  const diaryPath = isAdmin ? '/admin/calendar' : isSolicitor ? '/solicitor/calendar' : `${basePath}/diary`;
  // Barristers reach a contact via /barrister/clients/:id, everyone else via …/contacts/:id.
  const contactPath = (id: string) => (isBarrister ? `/barrister/clients/${id}` : `${basePath}/contacts/${id}`);
  // The contacts LIST and the create form follow the same split as contactPath: the barrister
  // shell calls them clients and registers no /contacts route at all. Both quick actions used to
  // derive from basePath, so a barrister was sent to /barrister/contacts and
  // /barrister/contacts/new — neither matches a route, and the catch-all then ejected them onto
  // the public marketing homepage. Exactly the fault already described for email hits below.
  const contactsPath = isBarrister ? '/barrister/clients' : `${basePath}/contacts`;
  const newContactPath = isBarrister ? '/barrister/clients/new' : `${basePath}/contacts/new`;
  // Communications is the route name in every tenant shell (/admin, /clerk, /barrister); only
  // the solicitor portal calls it Messages. `${basePath}/messages` matched no tenant route, so
  // this entry ejected an admin, a clerk and a barrister alike.
  const messagesPath = isSolicitor ? '/solicitor/messages' : `${basePath}/communications`;

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on Escape (window-level so it catches even if focus is on input)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Search via the real /search API. React Query guarantees only the LATEST
  // query's result is used (no stale-response race), and dedupes identical queries.
  const { data: searchData = [], isFetching } = useQuery({
    queryKey: ['global-search', debouncedQ, basePath],
    queryFn: async () => {
      const res = await api.get('/search', { params: { q: debouncedQ } });
      const d = (res.data as any)?.data ?? {};
      const rows: SearchResult[] = [];
      for (const m of (d.matters   ?? [])) rows.push({ ...m, to: `${basePath}/matters/${m.id}` });
      for (const doc of (d.documents ?? [])) rows.push({ ...doc, to: `${basePath}/documents` });
      for (const h of (d.hearings  ?? [])) rows.push({ ...h, to: `${basePath}/matters/${h.caseId}` });
      for (const msg of (d.messages ?? [])) rows.push({ ...msg, to: `${messagesPath}?thread=${msg.id}` });
      for (const ev of (d.events ?? [])) rows.push({ ...ev, to: diaryPath });
      // The solicitor PORTAL is deliberately smaller than a tenant shell: it has matters,
      // documents, the calendar, fee notes and messages, and nothing else. The search API
      // returns contact, judgment and task hits to any caller (access-scoped, but the TYPES come
      // back), and every one of those linked to a /solicitor/… route that does not exist — so a
      // solicitor clicking one was thrown out to the public marketing site. Offer them only
      // where there is somewhere to land.
      if (!isSolicitor) {
        for (const c of (d.contacts  ?? [])) rows.push({ ...c, to: contactPath(c.id) });
        for (const j of (d.judgments ?? [])) rows.push({ ...j, to: `${basePath}/judgments/${j.id}` });
        for (const t of (d.tasks ?? [])) rows.push({ ...t, to: t.caseId ? `${basePath}/matters/${t.caseId}` : `${basePath}/tasks` });
      }
      // `/inbox/search` matches NO route — the catch-all then ejected the user onto the
      // public marketing homepage, which reads as being logged out. The inbox exists only
      // under /barrister, so only offer email hits to a barrister.
      if (isBarrister) {
        for (const em of (d.emails ?? [])) rows.push({ ...em, to: `/barrister/inbox/search?q=${encodeURIComponent(em.label)}` });
      }
      return rows;
    },
    enabled: !!debouncedQ,
  });
  const results: SearchResult[] = debouncedQ ? searchData : [];
  const loading = isFetching && !!debouncedQ;

  // Reset selection when results/query changes
  useEffect(() => {
    setSelectedIdx(0);
  }, [results, debouncedQ]);

  // Quick actions — role-aware navigation + creation shortcuts
  const quickActions: QuickAction[] = [
    { id: 'nav-dashboard', label: 'Dashboard', sub: 'Go to your dashboard', Icon: House, to: basePath, group: 'navigate' },
    { id: 'nav-matters', label: 'Matters', sub: 'View all matters', Icon: Scales, to: `${basePath}/matters`, group: 'navigate' },
    ...(isSolicitor ? [] : [{ id: 'nav-hearings', label: 'Hearings', sub: 'View all hearings', Icon: Gavel, to: `${basePath}/hearings`, group: 'navigate' as const }]),
    { id: 'nav-calendar', label: 'Calendar', sub: 'Open diary / calendar', Icon: CalendarBlank, to: diaryPath, group: 'navigate' },
    { id: 'nav-messages', label: 'Messages', sub: 'Open communications', Icon: ChatTeardropDots, to: messagesPath, group: 'navigate' },
    ...(isSolicitor ? [] : [{ id: 'nav-tasks', label: 'Tasks', sub: 'View all tasks', Icon: ClipboardText, to: `${basePath}/tasks`, group: 'navigate' as const }]),
    ...(isAdmin || isClerk || isSolicitor || isBarrister ? [
      { id: 'nav-fees', label: 'Fees', sub: 'Fee records and notes', Icon: CurrencyGbp, to: `${basePath}/fees`, group: 'navigate' as const },
    ] : []),
    ...(isAdmin || isClerk || isBarrister ? [
      { id: 'nav-contacts', label: 'Contacts', sub: 'Clients and contacts', Icon: Users, to: contactsPath, group: 'navigate' as const },
    ] : []),
    ...(isSolicitor ? [] : [
      // ⌘K is the other way to start a matter, so it carries the same gate as the "+ New" menu —
      // a pupil holds no `case.create` and `POST /matters` answers 403. Hearing, contact and task
      // creation below are untouched: a pupil legitimately creates hearings and tasks.
      ...(canCreateMatter(user?.memberType)
        ? [{ id: 'create-matter', label: 'New matter', sub: 'Create a new matter', Icon: Plus, to: `${basePath}/matters/new`, group: 'create' as const }]
        : []),
      { id: 'create-hearing', label: 'New hearing', sub: 'Schedule a hearing', Icon: Plus, to: `${basePath}/hearings/new`, group: 'create' as const },
      { id: 'create-contact', label: 'New contact', sub: 'Add a client or contact', Icon: Plus, to: newContactPath, group: 'create' as const },
      { id: 'create-task', label: 'New task', sub: 'Add a task', Icon: Plus, to: `${basePath}/tasks`, group: 'create' as const },
    ]),
  ];

  // Filter quick actions by query (client-side only)
  const filteredActions = debouncedQ
    ? quickActions.filter(
        (a) =>
          a.label.toLowerCase().includes(debouncedQ.toLowerCase()) ||
          a.sub.toLowerCase().includes(debouncedQ.toLowerCase()),
      )
    : quickActions;

  // Combined list for keyboard navigation
  // When query is typed: show search results first, then matching actions
  // When query is empty: show only quick actions
  const navItems: Array<{ type: 'result'; item: SearchResult } | { type: 'action'; item: QuickAction }> =
    debouncedQ
      ? [
          ...results.map((r) => ({ type: 'result' as const, item: r })),
          ...filteredActions.map((a) => ({ type: 'action' as const, item: a })),
        ]
      : filteredActions.map((a) => ({ type: 'action' as const, item: a }));

  const handleSelect = useCallback(
    (entry: (typeof navItems)[number]) => {
      if (entry.type === 'result' && entry.item.to) {
        navigate(entry.item.to);
      } else if (entry.type === 'action') {
        navigate(entry.item.to);
      }
      onClose();
    },
    [navigate, onClose],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, navItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = navItems[selectedIdx];
      if (entry) handleSelect(entry);
    }
  };

  // Icon + colour per entity category
  const categoryMeta = (category: string) => {
    switch (category) {
      case 'Matter':   return { Icon: Briefcase,         style: 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400' };
      case 'Contact':  return { Icon: Users,             style: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400' };
      case 'Document': return { Icon: FileText,          style: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' };
      case 'Judgment': return { Icon: BookOpen,          style: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400' };
      case 'Hearing':  return { Icon: Gavel,             style: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' };
      case 'Message':  return { Icon: ChatTeardropDots,  style: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' };
      case 'Task':     return { Icon: ClipboardText,     style: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400' };
      case 'Event':    return { Icon: CalendarBlank,     style: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400' };
      case 'Email':    return { Icon: EnvelopeSimple,    style: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400' };
      default:         return { Icon: Briefcase,         style: 'bg-muted text-muted-foreground' };
    }
  };

  const navGroupLabel = debouncedQ && filteredActions.length > 0 && results.length > 0
    ? 'Quick actions'
    : debouncedQ
    ? 'Quick actions'
    : 'Navigation';

  const showCreateSection = !debouncedQ;

  // Split quick actions for the empty state (navigate / create)
  const navActions = filteredActions.filter((a) => a.group === 'navigate');
  const createActions = filteredActions.filter((a) => a.group === 'create');

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[10vh] backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <ScrollLock />
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border/60 bg-card shadow-2xl">
        {/* Input row */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <MagnifyingGlass className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search matters, hearings, contacts, or type a command…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
          {loading && <CircleNotch className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
          <button
            type="button"
            onClick={onClose}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Results area */}
        <div className="max-h-[60vh] overflow-y-auto">

          {/* ── Empty state: show organised quick actions ── */}
          {!debouncedQ && (
            <div className="py-1">
              {/* Navigate section */}
              {navActions.length > 0 && (
                <>
                  <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    Go to
                  </div>
                  {navActions.map((action, i) => {
                    const globalIdx = i;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => { navigate(action.to); onClose(); }}
                        onMouseEnter={() => setSelectedIdx(globalIdx)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-2 text-left transition',
                          selectedIdx === globalIdx ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-muted/60',
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <action.Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{action.label}</div>
                          <div className="text-xs text-muted-foreground">{action.sub}</div>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                      </button>
                    );
                  })}
                </>
              )}

              {/* Create section */}
              {showCreateSection && createActions.length > 0 && (
                <>
                  <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    Create
                  </div>
                  {createActions.map((action, i) => {
                    const globalIdx = navActions.length + i;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => { navigate(action.to); onClose(); }}
                        onMouseEnter={() => setSelectedIdx(globalIdx)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-2 text-left transition',
                          selectedIdx === globalIdx ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-muted/60',
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                          <action.Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{action.label}</div>
                          <div className="text-xs text-muted-foreground">{action.sub}</div>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* ── Search state: entity results + filtered actions ── */}
          {debouncedQ && (
            <>
              {/* Loading */}
              {loading && results.length === 0 && (
                <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
                  <CircleNotch className="h-4 w-4 animate-spin" /> Searching…
                </div>
              )}

              {/* Entity results */}
              {results.length > 0 && (
                <div className="py-1">
                  {results.map((item, i) => {
                    const prevCat = i > 0 ? results[i - 1]!.category : null;
                    const showHeader = item.category !== prevCat;
                    const { Icon, style } = categoryMeta(item.category);
                    return (
                      <div key={`${item.id}-${i}`}>
                        {showHeader && (
                          <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                            {item.category}s
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSelect({ type: 'result', item })}
                          onMouseEnter={() => setSelectedIdx(i)}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2 text-left transition',
                            selectedIdx === i ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-muted/60',
                          )}
                        >
                          <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', style)}>
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">{item.label}</div>
                            <div className="truncate text-xs text-muted-foreground">{item.sub}</div>
                          </div>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Matching quick actions */}
              {filteredActions.length > 0 && (
                <div className="py-1">
                  <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    {navGroupLabel}
                  </div>
                  {filteredActions.map((action, i) => {
                    const globalIdx = results.length + i;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => { navigate(action.to); onClose(); }}
                        onMouseEnter={() => setSelectedIdx(globalIdx)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-2 text-left transition',
                          selectedIdx === globalIdx ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-muted/60',
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <action.Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{action.label}</div>
                          <div className="text-xs text-muted-foreground">{action.sub}</div>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* No results at all */}
              {!loading && results.length === 0 && filteredActions.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No results for &ldquo;{query}&rdquo;
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-2 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5">Enter</kbd> open
            </span>
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5">Esc</kbd> close
            </span>
          </div>
          {debouncedQ && results.length > 0 && (
            <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
}
